<template>
  <v-row class="flex-column" no-gutters>
    <v-col class="shrink pt-2">
      <span
        class="text-body-2 valid-hint"
        :class="{
          'grey--text text--darken-2': isValid,
          'warning--text': !isValid,
        }"
      >
        {{ $t("page.viewSurvey.questions.MultipleChoice.tip") }}
      </span>
    </v-col>
    <v-col class="grow">
      <v-row
        ref="options"
        class="multichoicequestion options flex-grow-1 flex-wrap pt-2"
        justify="center"
        align="stretch"
        :class="{
          'single-row': singleRow,
        }"
      >
        <template v-if="question.optionsType === RepeatForOptions.BoardMembers">
          <v-col
            v-for="{
              individualId,
              individual: { preferredName, firstName, familyName },
            } in question.optionValues"
            :key="individualId"
            class="option flex-grow-0 d-flex justify-center"
            :style="optionStyle"
          >
            <div
              class="checkbox d-flex flex-column"
              :class="{
                selected: isSelected(individualId),
                disabled: isPreview,
              }"
              @click="selectAnswer(individualId)"
            >
              <div class="flex-grow-1 d-flex">
                <platform-avatar
                  v-if="individualId && questionnaire.companyId"
                  entity-type="individual"
                  class="avatar mb-2 mx-auto elevation-5"
                  placeholder-size="xl"
                  internal-loader
                  :size="null"
                  :style="avatarStyle"
                  :entity="{
                    id: individualId,
                    companyId: questionnaire.companyId,
                    preferredName,
                    firstName,
                    familyName,
                  }"
                />
              </div>
              <span class="flex-shrink-1 grey--text text--darken-3">
                {{ preferredName || firstName }}
              </span>
            </div>
          </v-col>
        </template>
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import { debounce } from 'lodash'
import { MultipleChoiceQuestionData } from '@/types/Question'
import { RepeatForOptions } from '@/helpers/questionnaire/questions'
import PlatformAvatar from '@/components/shared/PlatformAvatar.vue'
import { BlockPresetResponse } from '@betterboards/shared/helpers/entities/Questionnaire/index'
import {
  PlatformQuestionnaire,
  PlatformSurvey
} from '@betterboards/shared/types/Platform'

export default Vue.extend({
  name: 'SurveyMultipleChoiceQuestion',
  components: {
    PlatformAvatar
  },
  props: {
    question: {
      type: Object as PropType<MultipleChoiceQuestionData>,
      required: true
    },
    value: {
      type: [Array, String] as PropType<string[] | typeof BlockPresetResponse>,
      required: false
    }, // answers
    isPreview: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    RepeatForOptions,
    optionStyle: {
      width: <string | undefined>undefined,
      flexBasis: <string | undefined>undefined
    },
    avatarStyle: {
      maxWidth: <string | undefined>undefined,
      maxHeight: <string | undefined>undefined
    },
    resizeHandler: <undefined | (() => void)>undefined,
    answers: <any[]>[]
  }),
  mounted () {
    this.calculateStyle()
    this.resizeHandler = debounce(
      () => {
        this.calculateStyle()
        setTimeout(() => {
          this.calculateStyle()
        }, 500)
      },
      200,
      { leading: false, trailing: true }
    )
    if (Array.isArray(this.value)) {
      this.answers = this.value
    }
    if (this.resizeHandler) {
      window.visualViewport?.addEventListener('resize', this.resizeHandler)
    }
  },
  beforeDestroy () {
    if (this.resizeHandler) {
      window.visualViewport?.removeEventListener('resize', this.resizeHandler)
    }
  },
  watch: {
    isValid (val) {
      this.$emit('valid', val)
    },
    answers (val) {
      if (!this.isValid) {
        return
      }
      this.$emit('input', val)
    },
    value (val) {
      if (Array.isArray(val)) {
        this.answers = val
      }
    }
  },
  computed: {
    survey (): PlatformSurvey {
      return this.$store.state.Survey.survey
    },
    questionnaire (): PlatformQuestionnaire | null {
      if (!this.survey) {
        return null
      }

      return this.survey.questionnaire
    },
    isValid (): boolean {
      if (!this.answers || !Array.isArray(this.answers)) {
        return true
      }
      return this.answers.length <= 3
    },
    singleRow (): boolean {
      return (
        !!this.question.optionValues && this.question.optionValues.length <= 4
      )
    }
  },
  methods: {
    /**
     * The goal of this function is to attempt to use calculated flexBasis-es to force all elements
     *  onto a set number of rows, while retaining a flex layout.
     * On mobile ($vuetify.breakpoint.xsOnly), this changes to trying to fit all options into as close to a grid as possible.
     */
    calculateStyle (): void {
      const NumberOfLines = 2

      const containerEl: any | undefined = this.$refs.options
      if (!containerEl || !this.question.optionValues?.length) {
        return
      }

      const containerWidth = containerEl.getBoundingClientRect().width
      const isMobile = this.$vuetify.breakpoint.xsOnly

      const totalItems: number = this.question.optionValues.length
      let itemsPerRow: number | undefined
      if (isMobile) {
        itemsPerRow = Math.floor(Math.sqrt(totalItems))
      } else {
        itemsPerRow = this.singleRow
          ? totalItems
          : Math.ceil(totalItems / NumberOfLines)
      }
      const width = itemsPerRow
        ? `${Math.floor(containerWidth / itemsPerRow)}px`
        : undefined

      this.optionStyle = {
        width,
        flexBasis: width
      }

      // Scale avatar size based on number of items (more items = smaller avatars)
      // Base: 15vh for 1-3 items, scaling down to ~9vh for 8+ items
      const baseSize = 15
      const minSize = 9
      const scaleFactor = Math.max(0, Math.min(1, (totalItems - 3) / 5))
      const avatarSize = baseSize - (scaleFactor * (baseSize - minSize))
      this.avatarStyle = {
        maxWidth: `${avatarSize}vh`,
        maxHeight: `${avatarSize}vh`
      }
    },
    selectAnswer (answerId: string): void {
      if (this.isPreview) {
        return
      }
      const selected = this.answers ? [...this.answers] : []
      const index = selected.findIndex((answer) => answer === answerId)
      if (index === -1) {
        selected.push(answerId)
      } else {
        selected.splice(index, 1)
      }
      this.answers = selected
    },
    isSelected (answerId: string): boolean {
      return (
        Array.isArray(this.answers) &&
        this.answers.some((answer) => answer === answerId)
      )
    }
  }
})
</script>

<style lang="scss">
.valid-hint {
  transition: color ease-in-out 75ms;
}
.multichoicequestion.options {
  $minAvatarSize: 70px;
  &.single-row {
    .checkbox > span {
      font-size: 1em;
    }
  }
  .option {
    min-width: $minAvatarSize * 1.2;
    .checkbox {
      cursor: pointer;
      transition: transform 0.2s ease-out, padding ease-in-out 200ms;
      position: relative;

      &:hover:not(.disabled) {
        transform: scale(1.05) translateY(-4px);

        .avatar {
          box-shadow: 0 14px 28px -6px rgba(0, 0, 0, 0.45),
                      0 8px 14px rgba(0, 0, 0, 0.15),
                      inset 0 2px 4px rgba(255, 255, 255, 0.3),
                      inset 0 -3px 6px rgba(0, 0, 0, 0.2),
                      0 0 16px -6px var(--v-primary-lighten2);
        }
      }

      &.disabled {
        cursor: not-allowed;

        &:after {
          $padding: 4px;
          content: " ";
          position: absolute;
          left: -$padding;
          right: -$padding;
          top: -$padding;
          bottom: -$padding;
          background-color: var(--v-grey-darken1);
          opacity: 0.3;
          border-radius: 14px;
        }
      }

      &.selected {
        .avatar.v-avatar {
          border: 3px solid var(--color-selected-highlight);
          background-color: var(--color-selected-highlight);
          box-shadow: 0 10px 24px -4px rgba(0, 0, 0, 0.4),
                      0 6px 12px rgba(0, 0, 0, 0.12),
                      inset 0 2px 3px rgba(255, 255, 255, 0.25),
                      inset 0 -3px 6px rgba(0, 0, 0, 0.15),
                      0 0 18px -4px var(--color-selected-shadow);
        }
      }

      .avatar.v-avatar {
        aspect-ratio: 1;
        border-radius: 12px;
        min-width: $minAvatarSize;
        min-height: $minAvatarSize;
        transition: border-color 0.2s ease-out,
                    border-width 0.2s ease-out,
                    box-shadow 0.2s ease-out,
                    background-color 0.2s ease-out;
        border: 0 solid transparent;
        background-color: white;
        box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.35),
                    0 4px 8px rgba(0, 0, 0, 0.15),
                    inset 0 2px 3px rgba(255, 255, 255, 0.2),
                    inset 0 -3px 6px rgba(0, 0, 0, 0.2);
        overflow: hidden;

        .object-image {
          width: 100%;
          height: 100%;
        }

        img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      > span {
        font-size: 0.85em;
      }
    }
  }
}
</style>
